import { useRef, useState } from "react";
import TagManager from "react-gtm-module";
import { getStoredUtm } from "../../utils/utm";
import { sendEmailService } from "../../services/email.service.ts";
import styles from "./EmailForm.module.css";

const emptyData = { from_name: "", phone_number: "", company_name: "", email_address: "", message: "" };
const fields = [
  { name: "from_name", label: "שם מלא", type: "text", autoComplete: "name", required: true, hint: "כדי שנדע למי לחזור." },
  { name: "phone_number", label: "טלפון לחזרה", type: "tel", autoComplete: "tel", required: true, hint: "מספר שבו נוכל ליצור איתכם קשר.", dir: "ltr" },
  { name: "company_name", label: "שם העסק או המוסד (לא חובה)", type: "text", autoComplete: "organization", hint: "שם העסק יעזור לנו להתאים את ההצעה." },
  { name: "email_address", label: "אימייל (לא חובה)", type: "email", autoComplete: "email", hint: "אפשר להשאיר כתובת לקבלת פרטי ההצעה.", dir: "ltr" },
  { name: "message", label: "פרטי הפרויקט (לא חובה)", hint: "סוג המבנה, העיר וכמות המשתנות יעזרו לנו להתאים את ההצעה." },
];

const EmailForm = ({ formLocation }) => {
  const [data, setData] = useState(emptyData);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState("");
  const pending = useRef(false);
  const formRef = useRef(null);
  const prefix = `quote-${formLocation}-`;

  const submit = async (event) => {
    event.preventDefault();
    if (pending.current) return;
    setOutcome("");
    const next = {};
    if (!data.from_name.trim()) next.from_name = "שם הוא שדה חובה";
    if (!data.phone_number.trim()) next.phone_number = "טלפון הוא שדה חובה";
    if (data.email_address.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(data.email_address.trim())) next.email_address = "אימייל לא תקין";
    setErrors(next);
    if (Object.keys(next).length) {
      formRef.current.elements.namedItem(Object.keys(next)[0]).focus();
      return;
    }
    pending.current = true;
    setSending(true);
    const message = [
      formLocation === "home" ? "פנייה מטופס יצירת קשר בעמוד הבית (תחתית)" : "פנייה מטופס יצירת קשר",
      data.company_name.trim() && `שם העסק או המוסד: ${data.company_name.trim()}`,
      data.message.trim() && `הודעה מהלקוח:\n${data.message.trim()}`,
    ].filter(Boolean).join("\n\n");
    try {
      await sendEmailService({
        from_name: data.from_name.trim(),
        phone_number: data.phone_number.trim(),
        email_address: data.email_address.trim() || "לא סופק",
        message,
      });
      TagManager.dataLayer({ dataLayer: {
        event: "contact_us", form_location: formLocation, transaction_id: crypto.randomUUID(),
        user_data: { email: data.email_address, phone_number: data.phone_number }, ...getStoredUtm(),
      } });
      setData(emptyData);
      setOutcome("success");
    } catch {
      setOutcome("error");
    } finally {
      pending.current = false;
      setSending(false);
    }
  };

  const change = ({ target: { name, value } }) => {
    setData((previous) => ({ ...previous, [name]: value }));
    setErrors((previous) => ({ ...previous, [name]: undefined }));
  };

  return (
    <div className={styles.formWrapper}>
      <form ref={formRef} className={styles.contactForm} dir="rtl" onSubmit={submit} noValidate aria-busy={sending}>
        {fields.map(({ name, label, hint, ...inputProps }) => (
          <div className={styles.formGroup} key={name}>
            <label htmlFor={`${prefix}${name}`}>{label}{inputProps.required && " (חובה)"}</label>
            {name === "message" ? (
              <textarea id={`${prefix}${name}`} name={name} value={data[name]} onChange={change} maxLength={2000} aria-describedby={`${prefix}${name}-hint`} />
            ) : (
              <input {...inputProps} id={`${prefix}${name}`} name={name} value={data[name]} onChange={change}
                aria-required={!!inputProps.required} aria-invalid={!!errors[name]}
                aria-describedby={`${prefix}${name}-hint${errors[name] ? ` ${prefix}${name}-error` : ""}`} />
            )}
            <p id={`${prefix}${name}-hint`} className={styles.inputHint}>{hint}</p>
            {errors[name] && <p id={`${prefix}${name}-error`} className={styles.errorMessage} role="alert">{errors[name]}</p>}
          </div>
        ))}
        {outcome === "success" && <p className={styles.successMessage} role="status">הבקשה נשלחה בהצלחה. נחזור אליכם לבדיקת התאמה והצעת מחיר.</p>}
        {outcome === "error" && <div className={styles.errorAlert} role="alert">
          <p>אירעה תקלה בשליחת הבקשה. הפרטים נשמרו בטופס; אפשר לנסות שוב או ליצור קשר:</p>
          <a href="tel:0544494384" dir="ltr">0544494384</a>{" · "}<a href="https://wa.me/972525970820" target="_blank" rel="noopener noreferrer">דברו איתנו בוואטסאפ</a>
        </div>}
        <button className={styles.submitButton} disabled={sending} type="submit" aria-busy={sending}>{sending ? "שולח..." : "בקשת הצעת מחיר"}</button>
        <p className={styles.privacy}>נשתמש בפרטים כדי לחזור אליכם בנוגע לבקשה. ללא ספאם.</p>
      </form>
    </div>
  );
};

export default EmailForm;
